import type { TourStep } from './types';

export type TourId =
  | 'account_hub'
  | 'finance_deposit'
  | 'finance_withdrawals'
  | 'finance_wallet'
  | 'games'
  | 'settings'
  | 'transactions';

export interface TourDefinition {
  id: TourId;
  version: string;
  welcomeTitle: string;
  welcomeDescription: string;
  steps: TourStep[];
}

export function getTourForPath(pathname: string): TourDefinition | null {
  if (pathname === '/account') return accountHubTour;
  if (pathname === '/account/games') return gamesTour;
  if (pathname === '/account/finance/deposit') return depositTour;
  if (pathname === '/account/finance/withdrawals') return withdrawalsTour;
  if (pathname === '/account/finance/wallet') return walletTour;
  if (pathname === '/account/settings') return settingsTour;
  if (pathname === '/account/transactions') return transactionsTour;
  return null;
}

const v1 = 'v1';

const accountHubTour: TourDefinition = {
  id: 'account_hub',
  version: v1,
  welcomeTitle: 'Welcome to KioskGaming',
  welcomeDescription: 'Let me guide you step by step to get familiar with the main features.',
  steps: [
    {
      id: 'balance',
      targetTourId: 'post-login-tour.balance',
      title: 'Your Balance',
      description: 'Check your current balance here. You can refresh it anytime.',
      placement: 'bottom',
    },
    {
      id: 'notification',
      targetTourId: 'post-login-tour.notification',
      title: 'Notifications',
      description: 'View announcements and important updates here.',
      placement: 'bottom',
    },
    {
      id: 'language',
      targetTourId: 'post-login-tour.language',
      title: 'Language',
      description: 'Switch the app language anytime.',
      placement: 'bottom',
    },
    {
      id: 'deposit',
      targetTourId: 'post-login-tour.deposit',
      title: 'Deposit',
      description: 'Add funds to start playing. Select platforms and amount to deposit.',
      placement: 'bottom',
    },
    {
      id: 'withdraw',
      targetTourId: 'post-login-tour.withdraw',
      title: 'Withdraw',
      description: 'Withdraw funds from your game accounts back to your wallet.',
      placement: 'bottom',
    },
    {
      id: 'wallet',
      targetTourId: 'post-login-tour.wallet',
      title: 'Wallet',
      description: 'Manage your wallet balance, deposit to games, and request payouts.',
      placement: 'top',
    },
    {
      id: 'gameManagement',
      targetTourId: 'post-login-tour.game_management',
      title: 'Game management',
      description: 'Link and manage your game platforms in one place.',
      placement: 'top',
    },
    {
      id: 'transactions',
      targetTourId: 'post-login-tour.transactions',
      title: 'Transactions',
      description: 'View your full deposit and withdrawal history and track statuses.',
      placement: 'top',
    },
    {
      id: 'settings',
      targetTourId: 'post-login-tour.settings',
      title: 'Account Settings',
      description: 'Update your account details and replay this tour from the right menu.',
      placement: 'top',
    },
    {
      id: 'logout',
      targetTourId: 'post-login-tour.logout',
      title: 'Logout',
      description: 'Log out safely when you finish your session.',
      placement: 'top',
    },
  ],
};

const depositTour: TourDefinition = {
  id: 'finance_deposit',
  version: v1,
  welcomeTitle: 'Deposit Guide',
  welcomeDescription: 'Here is how to deposit to your game platforms.',
  steps: [
    {
      id: 'search',
      targetTourId: 'tour.deposit.search',
      title: 'Search games',
      description: 'Use search to quickly find a game platform.',
      placement: 'bottom',
    },
    {
      id: 'sort',
      targetTourId: 'tour.deposit.sort',
      title: 'Sort by balance',
      description: 'Sort platforms by current in-game balance to decide where to deposit.',
      placement: 'bottom',
    },
    {
      id: 'cards',
      targetTourId: 'tour.deposit.providers',
      title: 'Select platforms & amounts',
      description: 'Choose one or more platforms and set deposit amounts.',
      placement: 'top',
    },
    {
      id: 'cta',
      targetTourId: 'tour.deposit.cta',
      title: 'Deposit now',
      description: 'Proceed to checkout (or deposit directly if you have enough wallet balance).',
      placement: 'top',
    },
  ],
};

const withdrawalsTour: TourDefinition = {
  id: 'finance_withdrawals',
  version: v1,
  welcomeTitle: 'Withdrawal Guide',
  welcomeDescription: 'Here is how to withdraw from game platforms back to your wallet.',
  steps: [
    {
      id: 'search',
      targetTourId: 'tour.withdrawals.search',
      title: 'Search games',
      description: 'Use search to quickly find a game platform.',
      placement: 'bottom',
    },
    {
      id: 'sort',
      targetTourId: 'tour.withdrawals.sort',
      title: 'Sort by balance',
      description: 'Sort platforms by in-game balance so you can withdraw faster.',
      placement: 'bottom',
    },
    {
      id: 'cards',
      targetTourId: 'tour.withdrawals.providers',
      title: 'Select platforms & amounts',
      description: 'Pick platforms and set how much to withdraw (cannot exceed in-game balance).',
      placement: 'top',
    },
    {
      id: 'cta',
      targetTourId: 'tour.withdrawals.cta',
      title: 'Withdraw now',
      description: 'Submit withdrawal from selected game platforms to your wallet.',
      placement: 'top',
    },
  ],
};

const walletTour: TourDefinition = {
  id: 'finance_wallet',
  version: v1,
  welcomeTitle: 'Wallet Guide',
  welcomeDescription: 'Manage your wallet balance: add funds and request payouts.',
  steps: [
    {
      id: 'balanceCard',
      targetTourId: 'tour.wallet.balance_card',
      title: 'Wallet balance',
      description: 'See your available wallet balance and refresh it anytime.',
      placement: 'bottom',
    },
    {
      id: 'deposit',
      targetTourId: 'tour.wallet.deposit_section',
      title: 'Deposit to wallet',
      description: 'Enter an amount and continue to payment checkout to add funds to your wallet.',
      placement: 'top',
    },
    {
      id: 'payout',
      targetTourId: 'tour.wallet.payout_section',
      title: 'Payout from wallet',
      description: 'Request a payout when you are ready (account verification may be required).',
      placement: 'top',
    },
  ],
};

const transactionsTour: TourDefinition = {
  id: 'transactions',
  version: v1,
  welcomeTitle: 'Transactions Guide',
  welcomeDescription: 'Filter and review your deposit and withdrawal history.',
  steps: [
    {
      id: 'type',
      targetTourId: 'tour.transactions.type_filter',
      title: 'Filter by type',
      description: 'Show only deposits, only withdrawals, or all transactions.',
      placement: 'bottom',
    },
    {
      id: 'status',
      targetTourId: 'tour.transactions.status_filter',
      title: 'Filter by status',
      description: 'Narrow down transactions by status (pending, completed, failed, etc).',
      placement: 'bottom',
    },
    {
      id: 'list',
      targetTourId: 'tour.transactions.list',
      title: 'Transaction list',
      description: 'Tap a row to see details (or open payment link if available).',
      placement: 'top',
    },
    {
      id: 'pagination',
      targetTourId: 'tour.transactions.pagination',
      title: 'Pagination',
      description: 'Navigate through pages of transaction history.',
      placement: 'top',
    },
  ],
};

const gamesTour: TourDefinition = {
  id: 'games',
  version: v1,
  welcomeTitle: 'Games Guide',
  welcomeDescription: 'Search, filter, and open your game platforms.',
  steps: [
    {
      id: 'filters',
      targetTourId: 'tour.games.filters',
      title: 'Filters & search',
      description: 'Filter by linked/unregistered and search games quickly.',
      placement: 'bottom',
    },
    {
      id: 'grid',
      targetTourId: 'tour.games.grid',
      title: 'Game list',
      description: 'Browse your linked game accounts and balances here.',
      placement: 'top',
    },
    {
      id: 'pagination',
      targetTourId: 'tour.games.pagination',
      title: 'Pagination',
      description: 'Move between pages to see more games.',
      placement: 'top',
    },
  ],
};

const settingsTour: TourDefinition = {
  id: 'settings',
  version: v1,
  welcomeTitle: 'Settings Guide',
  welcomeDescription: 'Manage your credentials and personal information.',
  steps: [
    {
      id: 'credentials',
      targetTourId: 'tour.settings.credentials',
      title: 'Credentials',
      description: 'View your username and request new credentials if needed.',
      placement: 'bottom',
    },
    {
      id: 'contact',
      targetTourId: 'tour.settings.contact',
      title: 'Email & phone',
      description: 'Link your email/phone to secure your account.',
      placement: 'top',
    },
    {
      id: 'profile',
      targetTourId: 'tour.settings.profile',
      title: 'Profile',
      description: 'Edit your basic info and address details here.',
      placement: 'top',
    },
  ],
};

